// @ts-nocheck
/* Port of ReleaseDetailModal.js (241L). BYTE-IDENTICAL.
 * File-import button hooks into extractContentFromFile (pdf/docx) then
 * calls onImportContent(release, html). Content is defensively sanitised
 * via sanitizeReleaseHtml before injection.
 */
import { useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import {
  FiX,
  FiEdit2,
  FiSlash,
  FiCheck,
  FiUpload,
} from 'react-icons/fi';
import VersionBadge from './VersionBadge';
import ReleaseTypeBadge from './ReleaseTypeBadge';
import TagChip from './TagChip';
import QuickSummaryChecklist from './QuickSummaryChecklist';
import {
  formatReleaseDate,
  formatDateTime,
  sanitizeReleaseHtml,
} from './releaseUtils';
import { extractContentFromFile } from './releaseImport';

const ReleaseDetailModal = ({
  show,
  release,
  onHide,
  onShare,
  canManage,
  onImportContent,
  onEdit,
  onToggleDisable,
}: any) => {
  const safeHtml = useMemo(
    () => sanitizeReleaseHtml(release?.content_html),
    [release],
  );
  const fileRef = useRef<any>(null);
  const [importing, setImporting] = useState(false);

  const heroBtnStyle: any = {
    height: 36,
    background: 'rgba(255,255,255,0.16)',
    borderColor: 'rgba(255,255,255,0.3)',
    color: '#fff',
  };

  const onPickFile = async (e: any) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !release) return;
    setImporting(true);
    try {
      const { html, kind }: any = await extractContentFromFile(file);
      if (!html || !html.trim()) {
        toast.warning("Couldn't extract any content from that file.");
        return;
      }
      await onImportContent?.(release, html);
      toast.success(
        kind === 'pdf'
          ? 'PDF text imported into this release.'
          : 'Word document imported with images & links.',
      );
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  if (!release) return null;

  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className="sm:max-w-2xl rc-modal"
        aria-labelledby="rc-detail-title"
      >
      <div className="rc-modal__hero">
        <button
          type="button"
          className="rc-modal__close"
          onClick={onHide}
          aria-label="Close"
        >
          <FiX size={18} />
        </button>
        <div className="rc-modal__hero-meta">
          <VersionBadge version={release.version} />
          <ReleaseTypeBadge type={release.release_type} />
          {release.is_pinned && (
            <span className="rc-pin-badge">📌 Pinned</span>
          )}
          {!release.is_published && (
            <span className="rc-draft-badge">✎ Draft</span>
          )}
          {release.is_disabled && (
            <span className="rc-disabled-badge">⊘ Disabled</span>
          )}
        </div>
        <h2 className="rc-modal__title" id="rc-detail-title">
          {release.title}
        </h2>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div className="rc-modal__date">
            📅 {formatReleaseDate(release.release_date)}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {canManage && onEdit && (
              <button
                type="button"
                className="rc-btn rc-btn--ghost"
                style={heroBtnStyle}
                onClick={() => onEdit(release)}
                title="Edit this release"
              >
                <FiEdit2 style={{ marginRight: 6 }} /> Edit
              </button>
            )}
            {canManage && onToggleDisable && (
              <button
                type="button"
                className="rc-btn rc-btn--ghost"
                style={heroBtnStyle}
                onClick={() => onToggleDisable(release)}
                title={
                  release.is_disabled
                    ? 'Enable this release'
                    : 'Disable this release'
                }
              >
                {release.is_disabled ? (
                  <>
                    <FiCheck style={{ marginRight: 6 }} /> Enable
                  </>
                ) : (
                  <>
                    <FiSlash style={{ marginRight: 6 }} /> Disable
                  </>
                )}
              </button>
            )}
            {canManage && onImportContent && (
              <>
                <button
                  type="button"
                  className="rc-btn rc-btn--ghost"
                  style={heroBtnStyle}
                  disabled={importing}
                  onClick={() => fileRef.current?.click()}
                  title="Import content from a PDF or Word file into this release"
                >
                  {importing ? (
                    'Importing…'
                  ) : (
                    <>
                      <FiUpload style={{ marginRight: 6 }} /> Upload file
                    </>
                  )}
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  style={{ display: 'none' }}
                  onChange={onPickFile}
                />
              </>
            )}
            {/* {onShare && (
              <button
                type="button"
                className="rc-btn rc-btn--ghost"
                style={heroBtnStyle}
                onClick={() => onShare(release)}
                title="Copy a shareable link to this release (Super Admins only)"
              >
                🔗 Copy link
              </button>
            )} */}
          </div>
        </div>
      </div>

      <div className="rc-modal__body">
        {release.tags?.length > 0 && (
          <>
            <div className="rc-modal__section-label">Tags</div>
            <div className="rc-tags" style={{ marginTop: 0 }}>
              {release.tags.map((t: any) => (
                <TagChip key={t.id} tag={t} />
              ))}
            </div>
          </>
        )}

        {release.quick_summary && (
          <>
            <div className="rc-modal__section-label">Quick summary</div>
            <p className="rc-richtext" style={{ margin: 0 }}>
              {release.quick_summary}
            </p>
          </>
        )}

        {release.highlights?.length > 0 && (
          <>
            <div className="rc-modal__section-label">What changed</div>
            <QuickSummaryChecklist highlights={release.highlights} />
          </>
        )}

        {safeHtml && (
          <>
            <div className="rc-modal__section-label">Full release notes</div>
            {/* Authored by trusted Super Admins; sanitised defensively above. */}
            <div
              className="rc-richtext"
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
          </>
        )}

        {!safeHtml &&
          !release.highlights?.length &&
          !release.quick_summary && (
            <p
              className="rc-richtext"
              style={{ color: 'var(--rc-text-muted)' }}
            >
              No additional details for this release.
            </p>
          )}

        <div
          className="rc-edit-meta"
          style={{
            marginTop: 24,
            paddingTop: 14,
            borderTop: '1px dashed var(--rc-border)',
          }}
        >
          {release.edit_count > 0 ? (
            <>
              ✎ Edited {release.edit_count}{' '}
              {release.edit_count === 1 ? 'time' : 'times'} · last edited{' '}
              {formatDateTime(release.last_edited_at)}
              {release.editor?.username
                ? ` by ${release.editor.username}`
                : ''}
            </>
          ) : (
            <>Never edited since it was created.</>
          )}
        </div>
      </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReleaseDetailModal;
